export const listing = {
  sectionId: null,
  loading: false,
  totalPages: 1,
  currentPage: 1,
  maxPage: 1,
  filterMinPrice: '',
  filterMaxPrice: '',
  filterMin: 0,
  filterMax: 0,
  pagesLoaded: 1,

  // Register the popstate listener once (Alpine runs init() when the store is
  // registered). Back/forward re-derives the query from the URL and re-renders
  // without pushing a new history entry.
  init() {
    window.addEventListener('popstate', () => {
      if (!this.sectionId) return;

      this.loading = true;
      this.renderSection(window.location.search.replace(/^\?/, ''));
    });
  },

  // Resolve this listing's own section (Shopify wraps every section in #shopify-section-<id>)
  getSectionEl() {
    return document.getElementById(`shopify-section-${this.sectionId}`);
  },

  // Merge the filter and sort forms (separate DOM subtrees) so the URL keeps
  // both — rebuilding from just one drops the other's state.
  collectListingFormData() {
    const data = new FormData();

    for (const selector of ['[data-listing="filter"]', '[data-listing="sort"]']) {
      const form = document.querySelector(selector);

      if (!form) continue;

      for (const [key, value] of new FormData(form).entries()) {
        data.append(key, value);
      }
    }

    return data;
  },

  // Call section render API with data from filter
  async fetchAndRenderCollection(filterData) {
    const filterUrl = this.buildUrlFilter(filterData);

    // Get search term
    let searchUrl = new URL(location.href).searchParams.get('q');
    searchUrl = searchUrl ? `&q=${searchUrl}` : '';

    const urlParams = (filterUrl + searchUrl).replace(/^&/, '');
    history.pushState(null, '', `${window.location.pathname}${urlParams ? '?' + urlParams : ''}`);

    this.renderSection(urlParams);
  },

  // Fetch the section for a given query string and swap it in (no history change)
  async renderSection(params) {
    const query = params ? `&${params}` : '';

    try {
      const response = await fetch(`${window.location.pathname}?section_id=${this.sectionId}${query}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.text();

      const sectionElement = this.getSectionEl();

      if (sectionElement) {
        sectionElement.innerHTML = data;
      }
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.loading = false;
    }
  },

  // Check if next page is available and inject more products
  async fetchAndRenderPage(direction) {
    // Prevent browser from scrolling down
    history.scrollRestoration = 'manual';

    this.loading = true;

    // Update URL to show updated page number
    if (direction === 'next') {
      const url = new URL(window.location.href);
      url.searchParams.set('page', this.currentPage + 1);
      window.history.pushState({}, '', url);
    }

    // Get filter + sort data so paging preserves both
    const filterUrl = this.buildUrlFilter(this.collectListingFormData());

    // Get pagination count
    const pageUrl = `&page=${direction === 'next' ? this.currentPage + 1 : this.currentPage - 1}`;

    // Get search parameter
    const searchUrl = new URL(location.href).searchParams.get("q")
      ? `&q=${new URL(location.href).searchParams.get("q")}`
      : "";

    // Build fetch url
    const fetchUrl = `${window.location.pathname}?section_id=${this.sectionId}${pageUrl}${searchUrl}${filterUrl}`;

    // Check if we've reached the last page and we are not loading previous pages
    if (this.currentPage >= this.totalPages && direction !== 'previous') {
      this.loading = false;
      return;
    }

    try {
      const response = await fetch(fetchUrl);
      const data = await response.text();

      // Create a new HTML element and set its innerHTML to the fetched data
      const tempElement = document.createElement('div');
      tempElement.innerHTML = data;

      // Find the results within the fetched data
      const fetchedElement = tempElement.querySelector('[data-listing="results"]');
      const resultsElement = this.getSectionEl()?.querySelector('[data-listing="results"]');

      // If results are found, append its innerHTML to the existing element on the page
      if (fetchedElement && resultsElement) {
        if (direction === 'next') {
          resultsElement.insertAdjacentHTML('beforeend', fetchedElement.innerHTML);
        }

        if (direction === 'previous') {
          resultsElement.insertAdjacentHTML('afterbegin', fetchedElement.innerHTML);
        }
      }

      if (direction === 'next') {
        this.currentPage += 1;
        this.maxPage += 1;
        this.pagesLoaded += 1;
      } else {
        this.currentPage -= 1;
        const url = new URL(window.location.href);
        url.searchParams.set('page', this.currentPage);
        window.history.pushState({}, '', url);
      }

      this.loading = false;
    } catch (error) {
      console.error('Error:', error);
      this.loading = false;
    }
  },

  // Handle filter change (fired by either the filter form or the sort form)
  handleFilterChange() {
    this.loading = true;
    this.currentPage = 1;

    this.fetchAndRenderCollection(this.collectListingFormData());
  },

  // Handle deleting filters
  handleFilterDelete(filterToReset) {
    this.loading = true;

    const filterData = this.collectListingFormData();

    // Remove deleted filter
    filterData.delete(filterToReset);

    // Reset the price filters to their initial values
    if (filterToReset.includes('price')) {
      filterData.delete('filter.v.price.gte');
      filterData.delete('filter.v.price.lte');

      this.filterMinPrice = this.filterMin;
      this.filterMaxPrice = this.filterMax;
    }

    this.fetchAndRenderCollection(filterData);
  },

  // Handle deleting all filters (keep the active sort)
  handleFilterDeleteAll() {
    this.loading = true;

    const filterData = new FormData();
    const sort = document.querySelector('[data-listing="sort"]');

    if (sort) {
      for (const [key, value] of new FormData(sort).entries()) {
        filterData.append(key, value);
      }
    }

    this.fetchAndRenderCollection(filterData);
  },

  // Build urlFilter
  buildUrlFilter(filterData) {
    let urlFilter = '';

    for (const [key, value] of filterData.entries()) {
      // Handle non-price filters
      if (!key.includes('price')) {
        urlFilter += `&${key}=${encodeURIComponent(value.toString())}`;
        continue;
      }

      // Handle price filters
      if (
        (key === 'filter.v.price.lte' && value < this.filterMax) ||
        (key === 'filter.v.price.gte' && value > this.filterMin)
      ) {
        urlFilter += `&${key}=${value}`;
      }
    }

    return urlFilter;
  },

  // Method to scroll to the top of pagination
  scrollToTopOfPagination() {
    const element = this.getSectionEl();

    if (!element) return;

    element.scrollIntoView({ behavior: 'smooth', block: 'start' });
  },
};
